import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { formatDate } from '@utils';
import { medicalHistoryAndConditionsEndpoint } from './constants';

dayjs.extend(utc);

const formatAddressToPayload = (
  address,
  city,
  zip,
  state,
  isAddPayload,
  addressId = null,
  patientId = null,
) => {
  // NOTE if incomplete address is provided by user in the form, we must set value
  // of 'address' in payload to 'undefined', otherwise BE will reject the call
  if (address && city && zip && state) {
    if (isAddPayload) {
      return [
        {
          use: 'home',
          line: address ? [address] : [],
          city,
          postal_code: zip,
          // TODO make sure to get the country code a dynamic value
          country: 'US',
          state,
          type: 'physical',
        },
      ];
    }

    return [
      {
        id: addressId,
        use: 'home',
        line: address ? [address] : [],
        city,
        postal_code: zip,
        // TODO replace country code with dynamic value
        country: 'US',
        state,
        type: 'physical',
        patientId,
      },
    ];
  }
  return undefined;
};

export const formatAddressFromResponse = (addressRes = [{}]) => {
  // NOTE `type` & `use` currently do not need to be stored and are omitted
  /* eslint-disable-next-line no-unused-vars */
  const { line, postalCode: zip, type, use, ...rest } = addressRes[0];
  const formattedAddressResponse = {
    address: line?.[0],
    zip,
    ...rest,
  };

  return formattedAddressResponse;
};

export const demographicSectionAddPayload = ({
  firstName,
  lastName,
  phone,
  email,
  address,
  city,
  zip,
  state,
  birthDate,
  gender,
  sexForClinicalUse,
  raceEthnicity,
  patientMrn,
  personId = null,
  externalMedicalRecordNumber,
  podId,
}) => {
  const outputPayload = {
    personId,
    podId,
    name: [
      {
        use: 'official',
        family: lastName,
        given: [firstName],
      },
    ],
    deceasedBoolean: false,
    gender,
    sexForClinicalUse,
    raceEthnicity,
    birthDate: formatDate(birthDate?.replace(/[^a-z0-9,. ]/gi, '-'), 'YYYY-MM-DD'),
    externalMedicalRecordNumber: externalMedicalRecordNumber || patientMrn,
    // FIXME we shouldn't be doing validation processing here; separate concerns
    address: formatAddressToPayload(address, city, zip, state, true),
  };
  if (email || phone) {
    outputPayload.telecom = [];
    if (email) {
      outputPayload.telecom.push({
        system: 'email',
        value: email,
        use: 'home',
        rank: 1,
      });
    }
    if (phone) {
      outputPayload.telecom.push({
        system: 'phone',
        value: phone,
        use: 'home',
        rank: 2,
      });
    }
  }

  return outputPayload;
};

export const demographicSectionUpdatePayload = ({
  firstName,
  lastName,
  phone = null,
  email,
  address,
  state,
  birthDate,
  gender = null,
  patientMrn,
  sexForClinicalUse,
  city = '',
  zip = '',
  raceEthnicity = null,
  patientId = null,
  personId = null,
  nameId = null,
  emailId = null,
  phoneId = null,
  addressId = null,
  externalMedicalRecordNumber,
  withingsUserId = null,
  preferredTimeZone = null,
  enrollmentStatus = null,
  enrolledOn = null,
  carePathwayType = null,
}) => ({
  id: patientId,
  personId,
  patientId,
  name: [
    {
      id: nameId,
      use: 'official',
      family: lastName,
      given: [firstName],
      patientId,
    },
  ],
  telecom: [
    {
      id: emailId,
      system: 'email',
      value: email,
      use: 'home',
      rank: 1,
      patientId,
    },
  ].concat(
    phone
      ? {
          id: phoneId,
          system: 'phone',
          value: phone,
          use: 'home',
          rank: 2,
          patientId,
        }
      : [],
  ),
  deceasedBoolean: false,
  gender,
  sexForClinicalUse,
  raceEthnicity,
  withingsUserId,
  preferredTimeZone,
  enrollmentStatus,
  enrolledOn,
  carePathwayType,
  birthDate: formatDate(birthDate?.replace(/[^a-z0-9,. ]/gi, '-'), 'YYYY-MM-DD'),
  externalMedicalRecordNumber: externalMedicalRecordNumber || patientMrn,
  identifier: [],
  // FIXME we shouldn't be doing validation processing here; separate concerns
  address: formatAddressToPayload(address, city, zip, state, false, addressId, patientId) || [],
});

export const therapyDataPayload = (
  patientId,
  { code, display, value = null, valueString = null, component = [], text, status = 'final' } = {},
  valueQuantity = null,
) => {
  if ((code && display && value) || (code && display)) {
    const data = {
      status,
      type: 'therapy',
      patient_id: patientId,
      effectiveDateTime: dayjs.utc().toISOString(),
      identifier: [],
      code: {
        text: text || display,
        coding: [
          {
            code,
            system: 'PECC',
            display: text || display,
          },
        ],
      },
      valueBoolean: value,
      valueString,
      valueQuantity,
      component,
    };
    return {
      payload: data,
      endpoint: '/therapy_data',
    };
  }
  return null;
};

export const createMHCDataPayload = ({
  code,
  optionLabel,
  display = '',
  patientId = '',
  mhcId = '',
  usersMeData = {},
  status = 'active',
}) => {
  const determineAsserterType = (personaType) => {
    if (!personaType) return undefined;
    const lowerCasePersonaType = personaType.toLowerCase();

    if (lowerCasePersonaType === 'practitioner' || lowerCasePersonaType === 'clinician')
      return 'practitioner';
    if (lowerCasePersonaType === 'patient') return 'patient';
    return undefined;
  };

  if ((code && optionLabel) || (code && display)) {
    return {
      id: mhcId,
      patient_id: patientId,
      asserter_id: determineAsserterType(usersMeData?.personaType)
        ? usersMeData?.persona?.id
        : undefined,
      asserter_type: determineAsserterType(usersMeData?.personaType),
      code: {
        coding: {
          system: 'PECC',
          code,
          display: optionLabel || display,
        },
        text: optionLabel || display,
      },
      status,
    };
  } else {
    return {};
  }
};

export const constructValueQuantity = ({ value, unit, system }) => ({
  value,
  unit,
  code: { coding: { system, code: unit }, text: unit },
});

export const constructTherapyDataComponent = ({
  code,
  text = '',
  display = '',
  unit = '',
  value,
}) => {
  return {
    code: {
      text: text ?? display,
      coding: [
        {
          code,
          system: 'PECC',
          display,
        },
      ],
    },
    valueQuantity: constructValueQuantity({ value, unit, system: 'http://unitsofmeasure.org' }),
  };
};

export const createTherapyDataPayload = ({
  id = null,
  patientId = '',
  status = 'final',
  text = '',
  code = {},
  display = '',
  value = null,
  valueString = null,
  valueBoolean = null,
  valueQuantity = null,
  valueDateTime = null,
  valueInteger = null,
  component = [],
  category = null,
  schemaName = null,
}) => {
  return {
    id,
    identifier: [],
    effectiveDateTime: dayjs.utc().toISOString(),
    patientId,
    status,
    type: 'therapy',
    code: {
      text: text || display,
      coding: [
        {
          code,
          system: 'PECC',
          display: text || display,
        },
      ],
    },
    component,
    value,
    valueString,
    valueBoolean,
    valueQuantity,
    valueDateTime,
    valueInteger,
    ...(category !== null && { category }),
    ...(schemaName !== null && { schemaName }),
  };
};

export const medicalDiagnosisPayload = (diagnosis, patientId, usersMeData, mhcId) => {
  if ((diagnosis?.code && diagnosis?.optionLabel) || (diagnosis?.code && diagnosis?.display)) {
    const data = {
      id: mhcId,
      patient_id: patientId,
      asserter_id: usersMeData?.personaType === 'CLINICIAN' ? usersMeData?.persona?.id : undefined,
      asserter_type: usersMeData?.personaType === 'CLINICIAN' ? 'practitioner' : undefined,
      code: {
        coding: {
          system: 'PECC',
          code: diagnosis?.code,
          display: diagnosis?.optionLabel || diagnosis?.display,
        },
        text: diagnosis?.optionLabel || diagnosis?.display,
      },
      status: diagnosis?.status,
    };
    return {
      payload: data,
      endpoint: medicalHistoryAndConditionsEndpoint,
    };
  }
  return null;
};

export const pathwayInstancePayload = (
  optionLabel,
  patientId,
  pathwayTemplateId,
  id,
  status = 'active',
) => {
  if (optionLabel) {
    const data = {
      id: status === 'revoked' ? id : undefined,
      pathwayTemplateId,
      status,
      patientId,
    };
    return {
      payload: data,
      endpoint: '/pathway_instances',
    };
  }
  return null;
};
